import * as React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/DataTable'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { MoreHorizontal } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { usePlans } from '@/features/plans/api/get-plans'

type PlanRow = {
    planId?: string
    name: string
    description?: string | null
    features?: Array<{ featureKey?: string; featureId?: string; limitInt?: number; limitType?: string }>
}

const formatCentsToUSD = (value: unknown) => {
    const cents = Number(value)
    if (!Number.isFinite(cents)) return '—'
    const dollars = cents / 100
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars)
}

const columns: ColumnDef<PlanRow>[] = [
    {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => {
            const navigate = useNavigate()
            const plan = row.original as PlanRow
            return (
                <button
                    type='button'
                    className='font-medium text-primary hover:underline text-left'
                    onClick={() => plan.planId && navigate(`/plans/${plan.planId}`)}
                >
                    {row.getValue('name') as string}
                </button>
            )
        },
    },
    {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => <span className='text-muted-foreground'>{(row.getValue('description') as string) || '—'}</span>,
    },
    {
        accessorKey: 'recurringAmount',
        header: 'Recurring Amount',
        cell: ({ row }) => {
            const formatted = formatCentsToUSD(row.getValue('recurringAmount'))
            return <span className='text-muted-foreground'>{formatted}</span>
        },
    },
    {
        header: 'Features',
        cell: ({ row }) => {
            const features = (row.original as PlanRow).features ?? []
            if (!features.length) return <span className='text-muted-foreground'>—</span>
            return (
                <ul className='list-disc pl-5 text-muted-foreground'>
                    {features.map((f, i) => (
                        <li key={i}>{f.limitInt} {f.featureKey || f.featureId}</li>
                    ))}
                </ul>
            )
        },
    },
    {
        id: 'actions',
        enableHiding: false,
        cell: ({ row }) => {
            const plan = row.original as PlanRow
            const navigate = useNavigate()
            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant='ghost' className='h-8 w-8 p-0'>
                            <span className='sr-only'>Open menu</span>
                            <MoreHorizontal />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => navigate(`/plans/${plan.planId}`)}>
                            View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/plans/edit/${plan.planId}`)}>
                            Edit
                        </DropdownMenuItem>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <DropdownMenuItem
                                    className='text-destructive focus:text-destructive'
                                    onSelect={(e) => e.preventDefault()}
                                >
                                    Delete
                                </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete plan?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This action cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => {/* TODO: hook up delete */ }}>
                                        Confirm
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        },
    },
]

export default function PlansTable() {
    const { data, isLoading, error } = usePlans({})

    if (isLoading) return <div>Loading plans…</div>
    if (error) return <div className='text-destructive'>Failed to load plans.</div>

    return (
        <div className='w-full'>
            <DataTable columns={columns} data={(data as PlanRow[]) ?? []} emptyMessage='No plans.' />
        </div>
    )
}


